import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import MultiSelect from "../applicationUi/components/MultiSelect";
import { fetchClinicians } from "../store/thunks/cliniciansThunk";
import {
  checkMeetingConflicts,
  createMeeting,
  updateMeetingThunk,
  deleteMeetingThunk,
  checkMeetingResourceAvailability,
} from "../store/thunks/meetingsThunk";
import "react-datepicker/dist/react-datepicker.css";
import { XMarkIcon, CalendarIcon, ClockIcon, UserIcon, HomeIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@headlessui/react";

// Helper function to round time to nearest 5 minutes
const roundTimeToNearest5Minutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  const roundedMinutes = Math.round(minutes / 5) * 5;
  return `${hours.toString().padStart(2, "0")}:${roundedMinutes
    .toString()
    .padStart(2, "0")}`;
};

const QuickAddMeeting = ({
  isOpen,
  onClose,
  // For new meetings
  initialDate,
  initialTime,
  initialClinicianFname,
  initialClinicianLname,
  selectedClinician,
  selectedClinic,
  // For existing meetings
  selectedMeeting,
}) => {
  const dispatch = useDispatch();
  const clinicians = useSelector((state) => state.clinicians.clinicians);
  const user = useSelector((state) => state.auth.user);
  const resources = useSelector((state) => state.resources?.resources || []);
  const isUpdating = Boolean(selectedMeeting);

  console.log(
    "[QUICKADDMEETING] - This is the selected meeting:",
    selectedMeeting
  );

  // Initialize state based on whether we're creating or updating
  const [selectedDate, setSelectedDate] = useState(
    isUpdating
      ? new Date(selectedMeeting.date)
      : initialDate
      ? new Date(initialDate)
      : new Date()
  );

  const [selectedClinicians, setSelectedClinicians] = useState(
    isUpdating
      ? selectedMeeting.attendees.map((attendee) => ({
          value: attendee._id,
          label: `${attendee.firstName} ${attendee.lastName}`,
        }))
      : selectedClinician
      ? [
          {
            value: selectedClinician._id,
            label: `${selectedClinician.firstName} ${selectedClinician.lastName}`,
          },
        ]
      : []
  );

  const [meetingTitle, setMeetingTitle] = useState(
    isUpdating ? selectedMeeting.title : ""
  );
  const [meetingDescription, setMeetingDescription] = useState(
    isUpdating ? selectedMeeting.description : ""
  );
  const [meetingDuration, setMeetingDuration] = useState(
    isUpdating ? selectedMeeting.duration : 60
  );
  const [meetingTime, setMeetingTime] = useState(
    isUpdating
      ? selectedMeeting.startTime
      : initialTime
      ? roundTimeToNearest5Minutes(initialTime)
      : ""
  );
  const [resourceAvailability, setResourceAvailability] = useState({});
  const [selectedResources, setSelectedResources] = useState(
    isUpdating && selectedMeeting.resources ? selectedMeeting.resources : []
  );
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");

  useEffect(() => {
    dispatch(fetchClinicians());
  }, [dispatch]);

  // Fetch available resources when date, time, or duration changes
  useEffect(() => {
    if (selectedDate && meetingTime && meetingDuration && selectedClinic) {
      const [hours, minutes] = meetingTime.split(":").map(Number);
      const start = new Date(selectedDate);
      start.setHours(hours);
      start.setMinutes(minutes);

      dispatch(
        checkMeetingResourceAvailability({
          clinicId: selectedClinic._id,
          start: start.toISOString(),
          duration: meetingDuration,
        })
      ).then((action) => {
        // Store the availability response in a more easily accessible format
        const availabilityMap = {};
        action.payload.forEach((resource) => {
          availabilityMap[resource.resourceId] = {
            bookingStatus: resource.bookingStatus,
            overlappingBookings: resource.overlappingBookings
          };
        });
        setResourceAvailability(availabilityMap);
      });
    }
  }, [selectedDate, meetingTime, meetingDuration, selectedClinic, dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleClinicianChange = (selectedOptions) => {
    setSelectedClinicians(selectedOptions);
  };

  const handleTimeChange = (e) => {
    const roundedTime = roundTimeToNearest5Minutes(e.target.value);
    setMeetingTime(roundedTime);
  };

  const handleRemoveClinician = (clinicianId) => {
    setSelectedClinicians((prev) =>
      prev.filter((c) => c.value !== clinicianId)
    );
  };

  const handleResourceSelection = (resource) => {
    const availability = resourceAvailability[resource._id];
    if (!availability || availability.bookingStatus !== "Available") return;

    // Check if already selected
    const isAlreadySelected = selectedResources.some(
      (r) => r._id === resource._id
    );

    if (isAlreadySelected) {
      // Remove from selected resources
      setSelectedResources((prev) =>
        prev.filter((r) => r._id !== resource._id)
      );
    } else {
      // Add to selected resources
      setSelectedResources((prev) => [...prev, resource]);
    }
  };

  const handleBookAllDay = (event) => {
    event.preventDefault(); // Prevent unintended form submissions
    setMeetingTime("08:00"); // Set start time to 08:00
    setMeetingDuration(720); // Set duration to 12 hours
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const clinicId = isUpdating ? selectedMeeting.clinic : selectedClinic?._id;

    if (!clinicId) {
      console.error("Clinic is not selected or invalid.");
      alert("Please select a clinic before saving the meeting.");
      return;
    }

    const meetingData = {
      clinic: clinicId,
      title: meetingTitle,
      date: selectedDate,
      startTime: meetingTime,
      duration: meetingDuration,
      attendees: selectedClinicians.map((clinician) => clinician.value),
      resources: selectedResources.map(resource => resource._id),
      description: meetingDescription,
      createdBy: user._id,
    };

    const conflictCheckResult = await dispatch(
      checkMeetingConflicts(meetingData)
    );

    if (conflictCheckResult.payload?.conflicts?.length > 0) {
      if (
        !window.confirm(
          `${conflictCheckResult.payload.message}. Do you want to proceed?`
        )
      ) {
        return;
      }
    }

    try {
      if (isUpdating) {
        await dispatch(
          updateMeetingThunk({
            id: selectedMeeting._id,
            updatedData: meetingData,
          })
        );
      } else {
        await dispatch(createMeeting(meetingData));
      }
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isUpdating ? "update" : "create"} meeting:`,
        error
      );
    }
  };

  const handleDelete = async () => {
    if (!isUpdating) return;

    if (window.confirm("Are you sure you want to delete this meeting?")) {
      try {
        await dispatch(deleteMeetingThunk(selectedMeeting._id));
        onClose();
      } catch (error) {
        console.error("Failed to delete meeting:", error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-60 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />

        {/* This element is to trick the browser into centering the modal contents. */}
        <span
          className="inline-block h-screen align-middle"
          aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-lg rounded-sm">
          <div className="absolute right-4 top-4">
            <button
              type="button"
              className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={onClose}>
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          
          <div className="bg-white px-6 py-6">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-6 text-gray-900 mb-4">
                  {isUpdating ? "Update Meeting" : "Create New Meeting"}
                </Dialog.Title>
                <div className="mt-4">
                  <form onSubmit={handleSave}>
                    <div className="space-y-6">
                      <div className="border-b border-gray-200 pb-4">
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
                          {/* Title */}
                          <div className="sm:col-span-6">
                            <div className="flex items-center space-x-2 mb-2">
                              <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                              <label
                                htmlFor="meeting-title"
                                className="text-sm font-medium text-gray-900">
                                Meeting Title
                              </label>
                            </div>
                            <div>
                              <input
                                type="text"
                                name="meeting-title"
                                id="meeting-title"
                                value={meetingTitle}
                                onChange={(e) => setMeetingTitle(e.target.value)}
                                className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                placeholder="Enter meeting title..."
                              />
                            </div>
                          </div>

                          {/* Clinicians */}
                          <div className="sm:col-span-6">
                            <div className="flex items-center space-x-2 mb-2">
                              <UserIcon className="h-5 w-5 text-gray-500" />
                              <label
                                htmlFor="clinicians"
                                className="text-sm font-medium text-gray-900">
                                Attendees
                              </label>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedClinicians.map((clinician) => (
                                <span
                                  key={clinician.value}
                                  className="inline-flex items-center px-2.5 py-1 rounded-sm text-sm font-medium bg-indigo-50 text-indigo-700">
                                  {clinician.label}
                                  <button
                                    type="button"
                                    className="ml-1.5 text-indigo-400 hover:text-indigo-600"
                                    onClick={() =>
                                      handleRemoveClinician(clinician.value)
                                    }>
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </span>
                              ))}
                            </div>
                            
                            <div>
                              <MultiSelect
                                options={clinicians.map((clinician) => ({
                                  value: clinician._id,
                                  label: `${clinician.firstName} ${clinician.lastName}`,
                                }))}
                                selectedValues={selectedClinicians}
                                onChange={handleClinicianChange}
                                placeholder="Select attendees..."
                                className="w-full"
                              />
                            </div>
                          </div>
                          
                          {/* Description */}
                          <div className="sm:col-span-6">
                            <div className="flex items-center space-x-2 mb-2">
                              <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                              <label
                                htmlFor="meeting-description"
                                className="text-sm font-medium text-gray-900">
                                Description
                              </label>
                            </div>
                            <div>
                              <textarea
                                name="meeting-description"
                                id="meeting-description"
                                value={meetingDescription}
                                onChange={(e) =>
                                  setMeetingDescription(e.target.value)
                                }
                                rows="3"
                                className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                placeholder="Enter meeting details..."
                              />
                            </div>
                          </div>

                          {/* Date & Time section */}
                          <div className="sm:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {/* Date Column */}
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <CalendarIcon className="h-5 w-5 text-gray-500" />
                                <label
                                  htmlFor="meeting-date"
                                  className="text-sm font-medium text-gray-900">
                                  Date
                                </label>
                              </div>
                              <div>
                                <DatePicker
                                  selected={selectedDate}
                                  onChange={handleDateChange}
                                  dateFormat="MMMM d, yyyy"
                                  className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                />
                              </div>
                            </div>

                            {/* Time Column */}
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <ClockIcon className="h-5 w-5 text-gray-500" />
                                <label
                                  htmlFor="meeting-time"
                                  className="text-sm font-medium text-gray-900">
                                  Start Time
                                </label>
                              </div>
                              <div>
                                <input
                                  type="time"
                                  name="meeting-time"
                                  id="meeting-time"
                                  value={meetingTime}
                                  onChange={handleTimeChange}
                                  className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                  step="300" // 5-minute increments
                                />
                              </div>
                            </div>

                            {/* Duration Column */}
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <ClockIcon className="h-5 w-5 text-gray-500" />
                                <label
                                  htmlFor="meeting-duration"
                                  className="text-sm font-medium text-gray-900">
                                  Duration (mins)
                                </label>
                              </div>
                              <div>
                                <input
                                  type="number"
                                  name="meeting-duration"
                                  id="meeting-duration"
                                  value={meetingDuration}
                                  onChange={(e) =>
                                    setMeetingDuration(
                                      Math.max(
                                        5,
                                        Math.round(e.target.value / 5) * 5
                                      )
                                    )
                                  }
                                  className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                  step="5"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="sm:col-span-6">
                            <button
                              type="button"
                              className="mt-1 inline-flex items-center rounded-sm bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                              onClick={handleBookAllDay}
                              disabled={!selectedDate}>
                              <CalendarIcon className="h-4 w-4 mr-2" />
                              Book All Day (8AM - 8PM)
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Resources section */}
                      <div className="border-b border-gray-200 pb-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <HomeIcon className="h-5 w-5 text-gray-500" />
                          <h3 className="text-base font-semibold text-gray-900">Resources</h3>
                        </div>
                        
                        {selectedDate && meetingTime && meetingDuration ? (
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-sm text-gray-600">Select available resources for this meeting</p>
                              <select
                                value={resourceTypeFilter}
                                onChange={(e) => setResourceTypeFilter(e.target.value)}
                                className="rounded-sm border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-indigo-600 sm:text-sm">
                                <option value="">All Resources</option>
                                {Array.from(new Set(resources.map(r => r.type))).map(type => (
                                  <option key={type} value={type}>{type}</option>
                                ))}
                              </select>
                            </div>

                            <div className="space-y-3">
                              {Object.entries(
                                resources
                                  .filter((resource) =>
                                    resourceTypeFilter
                                      ? resource.type === resourceTypeFilter
                                      : true
                                  )
                                  .reduce((acc, resource) => {
                                    if (!acc[resource.type]) acc[resource.type] = [];
                                    acc[resource.type].push(resource);
                                    return acc;
                                  }, {})
                              ).length > 0 ? (
                                Object.entries(
                                  resources
                                    .filter((resource) =>
                                      resourceTypeFilter
                                        ? resource.type === resourceTypeFilter
                                        : true
                                    )
                                    .reduce((acc, resource) => {
                                      if (!acc[resource.type]) acc[resource.type] = [];
                                      acc[resource.type].push(resource);
                                      return acc;
                                    }, {})
                                ).map(([type, resources]) => (
                                  <div key={type} className="bg-gray-50 p-3 rounded-sm">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                                      {type}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {resources.map((resource) => {
                                        const availability = resourceAvailability[resource._id];
                                        return (
                                          <div
                                            key={resource._id}
                                            className="py-2 flex items-center">
                                            <input
                                              id={`resource-${resource._id}`}
                                              name={`resource-${resource._id}`}
                                              type="checkbox"
                                              checked={selectedResources.some(
                                                (r) => r._id === resource._id
                                              )}
                                              onChange={() => handleResourceSelection(resource)}
                                              disabled={!availability || availability.bookingStatus !== "Available"}
                                              className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ${
                                                !availability || availability.bookingStatus !== "Available" ? "opacity-50 cursor-not-allowed" : ""
                                              }`}
                                            />
                                            <label
                                              htmlFor={`resource-${resource._id}`}
                                              className="ml-3 block text-sm leading-6 text-gray-900">
                                              {resource.name}
                                              {resource.description && ` - ${resource.description}`}
                                              {resource.location && ` (${resource.location})`}
                                              <span className={`ml-2 ${
                                                !availability ? "text-gray-400" :
                                                availability.bookingStatus === "Available" 
                                                  ? "text-green-600" 
                                                  : availability.bookingStatus === "Unavailable"
                                                  ? "text-orange-600"
                                                  : "text-red-600"
                                              }`}>
                                                ({availability ? availability.bookingStatus : "Loading..."})
                                              </span>
                                            </label>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center py-4 text-gray-500">
                                  No resources found for the selected filter.
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Please select a date, time, and duration to view available resources.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex items-center justify-between gap-x-4">
                      {isUpdating && (
                        <button
                          type="button"
                          onClick={handleDelete}
                          className="rounded-sm bg-red-50 px-3 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-100">
                          Delete Meeting
                        </button>
                      )}
                      <div
                        className={`flex items-center gap-x-4 ${
                          !isUpdating ? "ml-auto" : ""
                        }`}>
                        <button
                          type="button"
                          onClick={onClose}
                          className="text-sm font-medium leading-6 text-gray-700 hover:text-gray-900">
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="rounded-sm bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                          {isUpdating ? "Update Meeting" : "Create Meeting"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default QuickAddMeeting;
